import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const courseOptions = [
  { value: "beginner_facial_course", label: "Beginner Facial Course ($45,000 GYD)" },
  { value: "advanced_facial_course", label: "Advanced Facial Course ($55,000 GYD)" },
  { value: "lip_care_course", label: "Lip Care Product Making Course ($60,000 GYD)" },
  { value: "beginners_nail_course", label: "Beginners Nail Course ($40,000 GYD)" },
  { value: "gelx_builder_course", label: "Gel-X & Builder Gel Nail Course ($50,000 GYD)" },
  { value: "acrylic_course", label: "Acrylic Nails Course ($50,000 GYD)" },
  { value: "advanced_nail_course", label: "Advanced Nail Course ($50,000 GYD)" },
  { value: "lash_course", label: "Lash Extension Course ($60,000 GYD)" },
  { value: "lash_lift_course", label: "Lash Lift Training Course ($45,000 GYD)" },
  { value: "waxing_course", label: "Body Waxing Course ($60,000 GYD)" },
  { value: "hair_course", label: "Hair Services Course ($55,000 GYD)" },
  { value: "advanced_hair_course", label: "Advanced Hair Course ($60,000 GYD)" },
  { value: "brow_training_course", label: "Brow Training Course ($40,000 GYD)" },
  { value: "wig_masterclass", label: "Wig Master Class ($80,000 GYD)" },
  { value: "multiple_courses", label: "Multiple Courses (We'll discuss)" },
];

const skillsOptions = [
  { value: "candle_making", label: "Candle Making Course ($50,000 GYD)" },
  { value: "complete_treat", label: "Complete Treat Course ($52,000 GYD)" },
  { value: "elementary_pastry", label: "Elementary Pastry Course ($25,000 GYD)" },
  { value: "cake_baking", label: "Cake Baking & Decoration ($65,000 GYD)" },
  { value: "multiple_skills", label: "Multiple Courses (We'll discuss)" },
];

const serviceOptions = [
  { value: "facial_service", label: "Facial Treatment" },
  { value: "nail_service", label: "Manicure / Pedicure / Nails" },
  { value: "lash_service", label: "Lash Extensions" },
  { value: "waxing_service", label: "Waxing Services" },
  { value: "hair_service", label: "Hair Services" },
  { value: "wig_service", label: "Wig Services" },
  { value: "other_service", label: "Other (Please specify)" },
];

const onDemandOptions = [
  { value: "facial_basics", label: "Facial Fundamentals ($15,000 GYD)" },
  { value: "nail_art_basics", label: "Nail Art for Beginners ($12,000 GYD)" },
  { value: "lash_theory", label: "Lash Extension Theory ($10,000 GYD)" },
  { value: "business_basics", label: "Starting Your Beauty Business ($18,000 GYD)" },
  { value: "candle_intro", label: "Introduction to Candle Making ($8,000 GYD)" },
  { value: "multiple_ondemand", label: "Multiple Courses (We'll discuss)" },
];

const specialOptions = [
  { value: "beginners_candle_special", label: "Beginners Candle Making ($45,000 GYD)" },
  { value: "advanced_candle_special", label: "Advanced Candle Making ($60,000 GYD)" },
  { value: "soap_making_special", label: "Soap Making Course ($60,000 GYD)" },
  { value: "body_butter_scrub_special", label: "Body Butter & Scrub Course ($60,000 GYD)" },
  { value: "multiple_special", label: "Multiple Courses (We'll discuss)" },
];

export const InquiryForm = ({ inquiryType = "training", defaultCourse = "facial_course" }) => {
  const isTraining = inquiryType === "training";
  const isSkills = inquiryType === "skills";
  const isService = inquiryType === "service";
  const isOnDemand = inquiryType === "ondemand";
  const isSpecial = inquiryType === "special";
  
  const getDefaultValue = () => {
    if (isSkills) return "candle_making";
    if (isService) return "facial_service";
    if (isOnDemand) return "facial_basics";
    if (isSpecial) return "beginners_candle_special";
    return defaultCourse;
  };
  
  const getOptions = () => {
    if (isSkills) return skillsOptions;
    if (isService) return serviceOptions;
    if (isOnDemand) return onDemandOptions;
    if (isSpecial) return specialOptions;
    return courseOptions;
  };
  
  const getLabel = () => {
    if (isSkills) return "Select Skills Course *";
    if (isService) return "Select Service *";
    if (isOnDemand) return "Select On-Demand Course *";
    if (isSpecial) return "Select Special Course *";
    return "Select Course *";
  };
  
  const getTitle = () => {
    if (isSkills) return "Skills Academy";
    if (isService) return "Book Appointment";
    if (isOnDemand) return "On-Demand Enrollment";
    if (isSpecial) return "Special Course Registration";
    return "Course Registration";
  };
  
  const getSubtitle = () => {
    if (isSkills) return "Register for Skills Course";
    if (isService) return "Request an Appointment";
    if (isSpecial) return "Register for Special Course";
    if (isOnDemand) return "Enroll in On-Demand Course";
    return "Express Your Interest";
  };
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    inquiry_type: getDefaultValue(),
    purpose: inquiryType,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/inquiries`, formData);

      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Inquiry submitted successfully!", {
          description: "We will contact you shortly.",
        });
        setFormData({ 
          name: "", 
          email: "", 
          phone: "", 
          message: "", 
          inquiry_type: getDefaultValue(),
          purpose: inquiryType 
        });
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#121212] border border-[#262626] p-8 md:p-12 text-center"
        data-testid="inquiry-success"
      >
        <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
        <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
          Thank You for Your Interest
        </h3>
        <p className="font-body text-[#A3A3A3] mb-8">
          We have received your inquiry and will contact you within 24 hours.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          data-testid="submit-another-btn"
          className="text-[#C5A059] font-body text-sm tracking-wider uppercase hover:underline"
        >
          Submit Another Inquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#121212] border border-[#262626] p-8 md:p-12"
      data-testid="inquiry-form"
    >
      <div className="mb-8">
        <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
          {getTitle()}
        </span>
        <h3 className="font-heading text-2xl md:text-3xl text-white mt-2">
          {getSubtitle()}
        </h3>
      </div>

      <div className="space-y-6">
        {/* Service/Course Selection */}
        <div>
          <label
            htmlFor="inquiry_type"
            className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2"
          >
            {getLabel()}
          </label>
          <select
            id="inquiry_type"
            name="inquiry_type"
            value={formData.inquiry_type}
            onChange={handleChange}
            required
            data-testid="input-type"
            className="w-full bg-transparent border-b border-white/20 text-white py-4 focus:border-[#C5A059] focus:outline-none appearance-none cursor-pointer"
            style={{ borderRadius: 0 }}
          >
            {getOptions().map((option) => (
              <option key={option.value} value={option.value} className="bg-[#121212] text-white">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2"
          >
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            data-testid="input-name"
            placeholder="Enter your full name"
            className="w-full luxury-input"
          />
        </div>

        {/* Email Field */}
        <div>
          <label
            htmlFor="email"
            className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2"
          >
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            data-testid="input-email"
            placeholder="Enter your email address"
            className="w-full luxury-input"
          />
        </div>

        {/* Phone Field */}
        <div>
          <label
            htmlFor="phone"
            className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2"
          >
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            data-testid="input-phone"
            placeholder="Enter your phone number"
            className="w-full luxury-input"
          />
        </div>

        {/* Message Field */}
        <div>
          <label
            htmlFor="message"
            className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2"
          >
            {isTraining ? "Additional Message" : "Preferred Date/Time & Notes"}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            data-testid="input-message"
            placeholder={isService ? "Let us know your preferred appointment time..." : "Any questions or special requirements..."}
            className="w-full luxury-input resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="submit-inquiry-btn"
          className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              {isService ? "Request Appointment" : "Submit Inquiry"}
            </>
          )}
        </button>
      </div>

      <p className="mt-6 font-body text-xs text-[#A3A3A3] text-center">
        {isService 
          ? "We work by appointment. We'll confirm your booking within 24 hours."
          : "By submitting this form, you agree to be contacted regarding the course."}
      </p>
    </form>
  );
};

export default InquiryForm;
